import React from 'react';
import styled from 'styled-components';

const SequenceStep = ({ step, templates, onUpdate, onRemove, isRemovable }) => {
  // Handle template selection change
  const handleTemplateChange = (e) => {
    onUpdate({
      ...step,
      templateId: e.target.value
    });
  };
  
  // Handle delay change
  const handleDelayChange = (e) => {
    const delay = parseInt(e.target.value, 10) || 0;
    onUpdate({
      ...step,
      delay: Math.min(Math.max(delay, 0), 30) // Ensure it's between 0 and 30
    });
  };
  
  return (
    <StepBox isInitial={step.isInitial}>
      {/* Step header with step label and remove button */}
      <StepHeader>
        <StepLabel>
          {step.isInitial ? 'Initial Email' : `Email ${step.delay} ${step.delay === 1 ? 'day' : 'days'} later`}
        </StepLabel>
        {isRemovable && (
          <RemoveButton 
            onClick={() => onRemove(step.id)}
            title="Remove this email from sequence"
          >
            ✕
          </RemoveButton>
        )}
      </StepHeader>
      
      {/* Template selection */}
      <FormGroup>
        <Label>Email Template</Label>
        <SelectInput
          value={step.templateId}
          onChange={handleTemplateChange}
          hasError={!step.templateId}
        >
          <option value="">Select a template</option>
          {templates.map(template => (
            <option key={template.id} value={template.id}>
              {template.name}
            </option>
          ))}
        </SelectInput>
      </FormGroup>
      
      {/* Delay settings (for non-initial steps) */}
      {!step.isInitial && (
        <FormGroup>
          <Label>Delay (days)</Label>
          <DelayInputContainer>
            <DelayInput
              type="number"
              min="1"
              max="30"
              value={step.delay}
              onChange={handleDelayChange}
            />
            <DelayLabel>days after previous email</DelayLabel>
          </DelayInputContainer>
        </FormGroup>
      )}
    </StepBox>
  );
};

export default SequenceStep;

// Styled Components
const StepBox = styled.div`
  width: 100%;
  max-width: 600px;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border: 1px solid #eee;
  transition: all 0.2s;
  
  /* Highlight the initial step with a subtle blue border */
  ${props => props.isInitial && `
    border-left: 4px solid #2196f3;
  `}
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
`;

const StepHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StepLabel = styled.div`
  font-weight: bold;
  color: #333;
`;

const RemoveButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #f44336;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  
  &:hover {
    background-color: #d32f2f;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #444;
  font-size: 14px;
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.hasError ? '#f44336' : '#ccc'};
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#f44336' : '#2196f3'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'rgba(244, 67, 54, 0.2)' : 'rgba(33, 150, 243, 0.2)'};
  }
`;

const DelayInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DelayInput = styled.input`
  width: 60px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
  }
  
  /* Remove arrows from number input */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const DelayLabel = styled.span`
  font-size: 14px;
  color: #666;
`; 